import { useEffect, useRef, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { create } from 'zustand'

import { paths } from 'src/config/paths'
import { Iconmark } from 'src/shared/components/icons/logos/iconmark'
import { PlusIcon } from 'src/shared/components/icons/sidebar/plus-icon'
import TabButton from 'src/shared/components/ui/navigation/tab-button'
import type { IconName } from 'src/shared/components/ui/navigation/tab-button-icon'
import { useMyUserData } from 'src/shared/features/users/api/accounts/use-my-user'
import ProfileAvatar from 'src/shared/features/users/components/profile-photos/profile-avatar'
import useBreakpoint from 'src/shared/hooks/use-breakpoint'
import { useHorizontalScroll } from 'src/shared/hooks/use-horizontal-scroll'
import { cn } from 'src/shared/utils/cn'

interface AppWindowFrameProps {
  children: React.ReactNode
}

interface TabButtonConfig {
  iconName: IconName
  filledIconName?: IconName
  name: string
  path: string
}

function TabletPortraitView({ tabButtons }: { tabButtons: TabButtonConfig[] }) {
  const { bpSwitch } = useBreakpoint()
  const { data: currentUser } = useMyUserData()

  const navigate = useNavigate()
  const location = useLocation()

  const mobileTabBarRef = useHorizontalScroll<HTMLDivElement>(250)

  return (
    <div
      className={cn(
        'flex h-[70px] w-full flex-row items-stretch justify-between transition-all duration-200 ease-out md:w-[70px] xl:w-[80px]',
      )}
    >
      <div className="flex min-w-[200px] shrink flex-row items-center">
        <Link to={paths.home.getHref()} className="ml-[15px] flex shrink-0 items-center justify-center">
          <Iconmark className="size-[28px] text-white md:size-[30px] xl:size-[35px]" />
        </Link>
        <div className="mx-[15px] min-h-[20px] w-px self-center bg-white/25" />
        <div className="flex min-h-[60px] min-w-[100px] flex-1 flex-row items-center">
          <div
            ref={mobileTabBarRef}
            className="flex w-full flex-row items-center overflow-x-auto overflow-y-hidden scrollbar-none"
          >
            <div className="flex w-full flex-row gap-1 md:gap-[19px] xl:gap-6">
              {tabButtons.map((tabButton) => (
                <TabButton
                  key={tabButton.name}
                  label={tabButton.name}
                  iconName={tabButton.iconName}
                  activeIconName={tabButton.filledIconName}
                  to={tabButton.path}
                  isActive={location.pathname.startsWith(tabButton.path)}
                  hideLabel={false}
                  iconClassName="scale-[0.75] md:scale-[0.92] xl:scale-100"
                  labelClassName="text-[12px] md:text-[13px] xl:text-[14px]"
                  className="flex-row py-0"
                  shouldLabelBeInsideHoverContainer={true}
                  hoverContainerClassName="h-auto w-auto py-[10px] pl-[10px] pr-3 group-hover:scale-100 gap-2"
                />
              ))}
            </div>
          </div>
          {/* {hasScroll && <div className="w-[1px] min-h-[40px] self-center bg-white/25" />} */}
        </div>
        <div className="ml-3 flex shrink-0 items-center justify-center">
          <div className="flex size-[40px] items-center justify-center rounded-full bg-white transition-all duration-200 hover:scale-110 hover:cursor-pointer md:size-[50px] xl:size-[55px]">
            <PlusIcon className="size-[12px] text-primary md:size-[15px] xl:size-[16.5px]" />
          </div>
        </div>
      </div>
      <div
        className="my-auto ml-3 flex shrink-0 flex-row gap-3 hover:cursor-pointer"
        onClick={() => {
          navigate(paths.settings.path)
        }}
      >
        <ProfileAvatar
          size={bpSwitch({
            xl: 50,
            md: 42,
            default: 37,
          })}
          userId={currentUser?.id ?? -1}
          className="box-content shrink-0 border-[3px] border-white"
        />
      </div>
    </div>
  )
}

interface AppWindowInsetsContextProps {
  insets: {
    top: number
    right: number
    bottom: number
    left: number
  }
  setInsets: (insets: { top: number; right: number; bottom: number; left: number }) => void
}

/** Used to get the insets/padding around the content of the page. */
export const useAppWindowInsetsStore = create<AppWindowInsetsContextProps>()((set) => ({
  insets: { top: 0, right: 0, bottom: 0, left: 0 },
  setInsets: (insets: { top: number; right: number; bottom: number; left: number }) => set({ insets }),
}))

export default function AppWindowFrame({ children }: AppWindowFrameProps) {
  const { setInsets } = useAppWindowInsetsStore()
  const { bpSwitch, isSmTabletOrSmaller, breakpoint } = useBreakpoint()
  const { data: currentUser } = useMyUserData()

  /** The conatiner that surrounds the content of the page. Does not include the sidebar. */
  const contentRef = useRef<HTMLDivElement>(null)

  const navigate = useNavigate()
  const location = useLocation()

  const [tabButtons] = useState<TabButtonConfig[]>([
    {
      iconName: 'briefcase',
      filledIconName: 'briefcaseFilled',
      name: 'Business',
      path: paths.referrals.list.path,
    },
    {
      iconName: 'house',
      filledIconName: 'houseFilled',
      name: 'Community',
      path: paths.home.path,
    },
    {
      iconName: 'chat',
      filledIconName: 'chatFilled',
      name: 'Messages',
      path: '/chats',
    },
    {
      iconName: 'bell',
      filledIconName: 'bellFilled',
      name: 'Activity',
      path: '/notifications',
    },
    {
      iconName: 'search',
      name: 'Search',
      path: paths.search.path,
    },
  ])

  const tabBarRef = useRef<HTMLDivElement>(null)

  // const horizontalScrollRef = useHorizontalScroll<HTMLDivElement>(30)
  const sidebarRef = useRef<HTMLDivElement>(null)

  const [isSidebarHovered, setIsSidebarHovered] = useState(false)

  // if the tab bar has scroll, we need to add a border to the bottom of the tab bar
  // const hasScroll = useMemo(() => {
  //   if (!tabBarRef.current) {
  //     return false
  //   }

  //   return (
  //     tabBarRef.current.scrollWidth > tabBarRef.current.clientWidth + 5 ||
  //     tabBarRef.current.scrollHeight > tabBarRef.current.clientHeight + 5
  //   )
  // }, [height, width])
  // TODO: Uncomment the code above and replace this. Note: Change the code above so that is doesn't use .current.
  const hasScroll = false

  // if the sidebar is hovered, we expand the sidebar
  useEffect(() => {
    const sidebar = sidebarRef.current
    if (!sidebar) {
      return
    }
    const handleMouseEnter = () => {
      setIsSidebarHovered(true)
    }
    const handleMouseLeave = () => {
      setIsSidebarHovered(false)
    }
    sidebar.addEventListener('mouseenter', handleMouseEnter)
    sidebar.addEventListener('mouseleave', handleMouseLeave)
    return () => {
      sidebar.removeEventListener('mouseenter', handleMouseEnter)
      sidebar.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [isSmTabletOrSmaller])

  useEffect(() => {
    if (contentRef.current) {
      const x = contentRef.current.getBoundingClientRect().x
      const y = contentRef.current.getBoundingClientRect().y
      const width = contentRef.current.getBoundingClientRect().width
      const height = contentRef.current.getBoundingClientRect().height

      setInsets({
        top: Math.ceil(y),
        left: Math.ceil(x),
        bottom: Math.ceil(window.innerHeight - height - y),
        right: Math.ceil(window.innerWidth - width - x),
      })
    }
  }, [isSidebarHovered, isSmTabletOrSmaller, breakpoint, setInsets])

  useEffect(() => {
    if (!contentRef.current) {
      return
    }
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const x = entry.target.getBoundingClientRect().x
        const y = entry.target.getBoundingClientRect().y
        const width = entry.target.getBoundingClientRect().width
        const height = entry.target.getBoundingClientRect().height

        setInsets({
          top: Math.ceil(y),
          left: Math.ceil(x),
          bottom: Math.ceil(window.innerHeight - height - y),
          right: Math.ceil(window.innerWidth - width - x),
        })
      }
    })
    observer.observe(contentRef.current)
    return () => {
      if (observer) {
        observer.disconnect()
      }
    }
  }, [contentRef, setInsets])

  return (
    <main
      className={cn(
        'flex max-h-screen w-full flex-row overflow-hidden bg-primary py-[20px] pr-[20px] xl:py-[29px] xl:pr-[26px]',
        isSmTabletOrSmaller && 'flex-col pl-[20px] pt-[0px]',
      )}
    >
      {/* <IOSDownloadPopup /> */}
      {/* <SidebarInset>{content}</SidebarInset> */}
      {/* <AppSidebar />
            <RightSidebar /> */}

      {isSmTabletOrSmaller ? (
        <TabletPortraitView tabButtons={tabButtons} />
      ) : (
        <div
          ref={sidebarRef}
          className={cn(
            'flex h-full flex-col items-stretch justify-between pb-[15px] transition-all duration-200 ease-out md:w-[70px] xl:w-[80px]',
            isSidebarHovered && 'px-2 md:w-[120px] xl:w-[140px]',
          )}
        >
          <div className="flex min-h-[240px] shrink flex-col items-center">
            <Link to={paths.home.getHref()} className="mb-3 flex w-full shrink-0 items-center justify-center py-[15px]">
              <Iconmark className="size-[28px] text-white md:size-[30px] xl:size-[35px]" />
            </Link>
            <div className="flex min-h-[100px] w-full flex-1 flex-col items-center overflow-hidden">
              <div ref={tabBarRef} className="flex w-full flex-col items-center overflow-y-auto py-4 scrollbar-none">
                <div className="flex w-full flex-col gap-[18px] md:gap-[19px] xl:gap-6">
                  {tabButtons.map((tabButton) => (
                    <TabButton
                      key={tabButton.name}
                      label={tabButton.name}
                      iconName={tabButton.iconName}
                      activeIconName={tabButton.filledIconName}
                      to={tabButton.path}
                      isActive={location.pathname.startsWith(tabButton.path)}
                      hideLabel={!isSidebarHovered}
                      iconClassName="scale-[0.75] md:scale-[0.92] xl:scale-100"
                      labelClassName="text-[12px] md:text-[13px] xl:text-[14px]"
                    />
                  ))}
                </div>
              </div>
              {hasScroll && <div className="h-px w-full bg-white/25" />}
            </div>
            <div className="mt-6 flex shrink-0 items-center justify-center">
              <div className="flex size-[40px] items-center justify-center rounded-full bg-white transition-all duration-200 hover:scale-110 hover:cursor-pointer md:size-[50px] xl:size-[55px]">
                <PlusIcon className="size-[12px] text-primary md:size-[15px] xl:size-[16.5px]" />
              </div>
            </div>
          </div>
          <div
            className="mt-10 flex shrink-0 flex-col items-center gap-3 hover:cursor-pointer"
            onClick={() => {
              navigate(paths.settings.path)
            }}
          >
            <ProfileAvatar
              size={bpSwitch({
                xl: 50,
                md: 42,
                default: 37,
              })}
              userId={currentUser?.id ?? -1}
              className="box-content shrink-0 border-[3px] border-white"
            />
            <div
              className={cn(
                'text-sm font-semibold text-white transition-all duration-300',
                !isSidebarHovered && 'opacity-0',
              )}
            >
              Menu
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="w-full flex-1 overflow-hidden rounded-[25px] bg-white" ref={contentRef}>
        <div
          className="size-full flex-1 overflow-y-auto [&::-webkit-scrollbar-thumb]:rounded-full
                [&::-webkit-scrollbar-thumb]:bg-[#606F6A]
              [&::-webkit-scrollbar-track]:my-5
                [&::-webkit-scrollbar-track]:rounded-full
                [&::-webkit-scrollbar-track]:bg-[#D8D8D6]
                [&::-webkit-scrollbar]:w-[5px]"
        >
          {/* <div className={cn('px-[30px] transition-all duration-500 ease-out', isSidebarHovered && 'px-[0px]')}> */}
          {children}
          {/* </div> */}
        </div>
      </div>
    </main>
  )
}
